import { InsuranceFormValues } from "@/components/Patient/Insurance/insuranceSchema";
import type useFileManager from "@/hooks/useFileManager";
import {
  CoverageTypeRecord,
  GovernmentSchemeRecord,
  PatientFileRecord,
} from "@/types/emr/patient/patientMasterData";
import { format } from "date-fns";
import { Download, Eye, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { FileCategory, FileReadMinimal, FileType } from "@/types/files/file";

type InsuranceTableProps = {
  records: InsuranceFormValues[];
  patientId?: string;

  coverageTypes: CoverageTypeRecord[];
  governmentSchemes: GovernmentSchemeRecord[];

  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  fileManager: ReturnType<typeof useFileManager>;
};

const InsuranceTable = ({
  records,
  patientId,
  coverageTypes,
  governmentSchemes,
  onEdit,
  onDelete,
  fileManager,
}: InsuranceTableProps) => {
  const getCoverageTypeName = (id?: string | null) =>
    coverageTypes.find((item) => item.id === id)?.name ?? "-";

  const { t } = useTranslation();

  if (records.length === 0) {
    return null;
  }

  const viewDocument = (
    file?: PatientFileRecord | null,
    pending?: File | null,
  ) => {
    if (pending) {
      const url = URL.createObjectURL(pending);

      window.open(url, "_blank", "noopener,noreferrer");

      setTimeout(() => URL.revokeObjectURL(url), 1000);

      return;
    }

    if (!file?.id || !patientId) {
      return;
    }

    const uploaded: FileReadMinimal = {
      id: file.id,
      name: file.name,
      extension: file.extension,
      file_type: FileType.PATIENT,
      file_category: FileCategory.UNSPECIFIED,
      associating_id: patientId,
      mime_type: "",
      archived_by: null as never,
      archived_datetime: "",
      upload_completed: true,
      created_date: "",
      uploaded_by: null as never,
    };
    void fileManager.viewFile(uploaded, patientId);
  };

  const downloadDocument = (file?: PatientFileRecord | null) => {
    if (!file?.id || !patientId) {
      return;
    }

    const uploaded: FileReadMinimal = {
      id: file.id,
      name: file.name,
      extension: file.extension,
      file_type: FileType.PATIENT,
      file_category: FileCategory.UNSPECIFIED,
      associating_id: patientId,
      mime_type: "",
      archived_by: null as never,
      archived_datetime: "",
      upload_completed: true,
      created_date: "",
      uploaded_by: null as never,
    };

    void fileManager.downloadFile(uploaded, patientId);
  };

  const getGovernmentSchemeName = (record: InsuranceFormValues) => {
    if (record.government_scheme_other?.trim()) {
      return record.government_scheme_other;
    }

    return (
      governmentSchemes.find((item) => item.id === record.government_scheme)
        ?.name ?? "-"
    );
  };

  return (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className="min-w-[1400px] w-full text-sm border-collapse">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
              {t("coverage_type")}
            </th>

            <th className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
              {t("government_scheme")}
            </th>

            <th className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
              {t("insurance_provider_name")}
            </th>

            <th className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
              {t("tpa_name")}
            </th>

            <th className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
              {t("policy_or_card_number")}
            </th>

            <th className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
              {t("policy_validity_date")}
            </th>

            <th className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
              {t("pre_authorization_required")}
            </th>

            <th className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
              {t("document")}
            </th>

            <th className="px-4 py-3 text-center whitespace-nowrap">
              {t("actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((item, index) => (
            <tr key={item.id ?? item.localId} className="border-t">
              <td
                className="px-4 py-3 text-center border-r border-gray-200"
                title={getCoverageTypeName(item.coverage_type)}
              >
                <div className="mx-auto max-w-[180px] truncate">
                  {getCoverageTypeName(item.coverage_type)}
                </div>
              </td>

              <td
                className="px-4 py-3 text-center border-r border-gray-200"
                title={getGovernmentSchemeName(item)}
              >
                <div className="mx-auto max-w-[180px] truncate">
                  {getGovernmentSchemeName(item)}
                </div>
              </td>

              <td
                className="px-4 py-3 text-center border-r border-gray-200"
                title={item.insurance_provider_name || "-"}
              >
                <div className="mx-auto max-w-[180px] truncate">
                  {item.insurance_provider_name || "-"}
                </div>
              </td>

              <td
                className="px-4 py-3 text-center border-r border-gray-200"
                title={item.tpa_name || "-"}
              >
                <div className="mx-auto max-w-[180px] truncate">
                  {item.tpa_name || "-"}
                </div>
              </td>

              <td
                className="px-4 py-3 text-center border-r border-gray-200"
                title={item.policy_or_card_number || "-"}
              >
                <div className="mx-auto max-w-[180px] truncate">
                  {item.policy_or_card_number || "-"}
                </div>
              </td>

              <td className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
                {item.policy_validity_date
                  ? format(new Date(item.policy_validity_date), "dd/MM/yyyy")
                  : "-"}
              </td>

              <td className="px-4 py-3 text-center whitespace-nowrap border-r border-gray-200">
                {item.pre_authorization_required === undefined
                  ? "-"
                  : item.pre_authorization_required
                    ? t("yes")
                    : t("no")}
              </td>

              <td className="px-4 py-3 whitespace-nowrap text-center border-r border-gray-200">
                {item.insurance_card_letter_file ||
                item.insurance_card_letter_pending ? (
                  <div className="flex justify-center gap-3">
                    <button
                      type="button"
                      title={
                        item.insurance_card_letter_pending?.name ??
                        item.insurance_card_letter_file?.name ??
                        ""
                      }
                      className="text-primary hover:text-primary/80"
                      onClick={() =>
                        viewDocument(
                          item.insurance_card_letter_file,
                          item.insurance_card_letter_pending,
                        )
                      }
                    >
                      <Eye className="h-4 w-4" />
                    </button>

                    {patientId &&
                      item.insurance_card_letter_file &&
                      !item.insurance_card_letter_pending && (
                        <button
                          type="button"
                          title={item.insurance_card_letter_file.name}
                          className="text-primary hover:text-primary/80"
                          onClick={() =>
                            downloadDocument(item.insurance_card_letter_file)
                          }
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                  </div>
                ) : (
                  "-"
                )}
              </td>

              <td className="px-4 py-3 whitespace-nowrap text-center">
                <div className="flex justify-center gap-3">
                  <button
                    type="button"
                    title={t("edit")}
                    className="text-primary hover:text-primary/80"
                    onClick={() => onEdit(index)}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    title={t("delete")}
                    className="text-red-600 hover:text-red-500"
                    onClick={() => onDelete(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InsuranceTable;
